import Heading from '~/components/Shared/Heading/Heading';
import Page from '~/components/Shared/Layout/Page/Page';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { instance } from '~/utils/api/api';
import PromotionForm from '~/components/PromotionForm/PromotionForm';
import { useTranslation } from 'react-i18next';
import { useToastError } from '~/utils/useToastError';
import { Spinner } from '~/ui';
import { useToastSuccess } from '~/utils/useToastSuccess';
import { ICreatePromotionProps } from '../CreatePromotion/CreatePromotion';
import { getPaginate } from '~/utils/getPaginate';
import * as Tabs from '@radix-ui/react-tabs';
import cn from 'classnames';
import styles from './EditPromotionPage.module.scss';
import PointsBoard from '~/components/PromotionsBoard/PointsBoard/PointsBoard';
import { useState } from 'react';

const EditPromotionPage = () => {
  const { stockId } = useParams();
  const [activeTab, setActiveTab] = useState('info');
  const navigate = useNavigate();
  const client = useQueryClient();
  const toastError = useToastError();
  const toastSuccess = useToastSuccess();
  const { t } = useTranslation();
  const { value, paginate } = getPaginate();

  const { mutateAsync: updatePromotionMethod } = useMutation(
    async ({
      name,
      description,
      start_date,
      end_date,
      burn_count_days,
      calculated_points,
      piwigo_image_id,
      accommodation_ids,
      organization_ids,
      rules,
    }: ICreatePromotionProps) => {
      const response = await instance.put(`admin/promo-loyalty/${stockId}`, {
        loyalty_promo: {
          name,
          description,
          burn_count_days,
          start_date,
          end_date,
          calculated_points,
          piwigo_image_id,
          accommodation_ids,
          organization_ids,
        },
        rules,
      });

      return response.data;
    },
    {
      onSuccess: () => {
        client.invalidateQueries(['promotion']);
        toastSuccess(t('stock_successfull_updated'));
        navigate(`/promotions?${value}=${paginate}`);
      },
      onError: error => {
        toastError(error);
      },
    }
  );

  const { data, isLoading } = useQuery({
    queryFn: async () => {
      const { data } = await instance.get(`admin/promo-loyalty/${stockId}`);
      return data;
    },
    queryKey: ['promotion', stockId],
    onError: error => {
      toastError(error);
    },
  });

  return (
    <Page heading={<Heading text={t('edit_promotion')} />}>
      <Tabs.Root className={styles.tabsRoot} value={activeTab} onValueChange={setActiveTab}>
        <Tabs.List className={styles.tabsList}>
          <Tabs.Trigger
            value="info"
            className={cn(styles.tabsTrigger, { [styles.tabsTriggerActive]: activeTab === 'info' })}
          >
            {t('general_information')}
          </Tabs.Trigger>
          <Tabs.Trigger
            value="history"
            className={cn(styles.tabsTrigger, { [styles.tabsTriggerActive]: activeTab === 'history' })}
          >
            {t('points_history')}
          </Tabs.Trigger>
        </Tabs.List>
        <Tabs.Content tabIndex={undefined} className={styles.tabsContent} value="info">
          {!isLoading ? (
            <PromotionForm
              isEdit
              data={data}
              onSubmit={updatePromotionMethod}
            />
          ) : (
            <Spinner />
          )}
        </Tabs.Content>
        <Tabs.Content tabIndex={undefined} className={styles.tabsContent} value="history">
          <PointsBoard promoLoyaltyId={Number(stockId)} />
        </Tabs.Content>
      </Tabs.Root>
    </Page>
  );
};

export default EditPromotionPage;
